import {
  Badge,
  Box,
  Collapse,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { type ReactNode, useState } from 'react';
import { PiCaretDown, PiCaretRight } from 'react-icons/pi';
import Link from '@/components/Link';
import { formatDate, formatPrice } from '@/lib/helpers';
import {
  NOT_PAID_REASONS,
  type PortfolioService,
  type ServiceCycle,
} from '@/models/clientCompanies';
import {
  CLIENT_PORTFOLIO_FULL,
  useHasAnyPermission,
} from '@/models/permissions';
import { CycleEdit } from '@/components/client-portfolio/CycleEdit';
import { ServiceActivity } from '@/components/client-portfolio/ServiceActivity';
import { ServiceHistory } from '@/components/client-portfolio/ServiceHistory';

const PERIOD_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  biannually: 'Every 6 months',
  annually: 'Annual',
  biennial: 'Every 2 years',
};

const ORIGIN_LABELS: Record<PortfolioService['origin'], string> = {
  offer: 'Offer',
  monday_company: 'Monday company',
  monday_deal: 'Monday deal',
};

const date = (v: string | null) => (v ? formatDate(v) : '-');

const money = (
  price: string | number | null,
  currency: string | null,
  units?: string | number | null,
) => {
  if (price == null) return '-';
  const one = formatPrice(Number(price), currency ?? '');
  const n = units == null ? 1 : Number(units);
  return n === 1 ? one : `${n} x ${one}`;
};

export function DeliveryBadge({ service }: { service: PortfolioService }) {
  if (service.deliveryState === 'pending')
    return (
      <Badge size="sm" color="orange" variant="light">
        Not delivered
      </Badge>
    );
  if (service.deliveredSource === 'monday')
    return (
      <Badge size="sm" color="teal" variant="light">
        Delivered (Monday)
      </Badge>
    );
  return (
    <Tooltip
      label={`${service.deliveredByName ?? 'Unknown user'}, ${date(service.deliveredAt)}`}
    >
      <Badge size="sm" color="green" variant="light">
        Delivered {date(service.deliveredAt)}
      </Badge>
    </Tooltip>
  );
}

export function PortfolioList({
  services,
  canWrite,
  readOnly = false,
  actions,
  title = 'Portfolio',
}: {
  services: PortfolioService[];
  canWrite: boolean;
  readOnly?: boolean;
  actions?: (service: PortfolioService) => ReactNode;
  title?: string;
}) {
  const pending = services.filter((s) => s.deliveryState === 'pending').length;
  const unmapped = services.filter(
    (s) => s.mappingStatus === 'unmapped',
  ).length;
  const [filter, setFilter] = useState<'all' | 'pending'>('all');
  const shown =
    filter === 'pending'
      ? services.filter((s) => s.deliveryState === 'pending')
      : services;

  return (
    <Paper withBorder p="sm">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <Title order={5}>{title}</Title>
            <Text fz="xs" c="dimmed">
              {services.length} services
            </Text>
            {pending > 0 && (
              <Badge size="xs" color="orange" variant="light">
                {pending} not delivered
              </Badge>
            )}
            {unmapped > 0 && (
              <Badge size="xs" color="yellow" variant="light">
                {unmapped} unmapped
              </Badge>
            )}
          </Group>
          {pending > 0 && pending < services.length && (
            <SegmentedControl
              size="xs"
              value={filter}
              onChange={(v) => setFilter(v as typeof filter)}
              data={[
                { value: 'all', label: 'All' },
                { value: 'pending', label: 'Not delivered' },
              ]}
            />
          )}
        </Group>
        {shown.length === 0 && (
          <Text fz="sm" c="dimmed">
            No services
          </Text>
        )}
        {shown.map((s) => (
          <ServiceRow
            key={s.id}
            service={s}
            canWrite={canWrite && !readOnly}
            readOnly={readOnly}
            actions={actions}
          />
        ))}
      </Stack>
    </Paper>
  );
}

function ServiceRow({
  service: s,
  canWrite,
  readOnly,
  actions,
}: {
  service: PortfolioService;
  canWrite: boolean;
  readOnly: boolean;
  actions?: (service: PortfolioService) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const Icon = open ? PiCaretDown : PiCaretRight;
  const canOpenOffers = useHasAnyPermission([
    'offers:read',
    'offers:fulfill',
    'offers:*',
    '*:*',
  ]);
  const fullAccess = useHasAnyPermission(CLIENT_PORTFOLIO_FULL);
  const recurring = s.kind === 'recurring';

  return (
    <Paper
      withBorder
      p="xs"
      ml={s.parentId ? 'lg' : 0}
      bg={
        s.deliveryState === 'pending'
          ? undefined
          : 'var(--mantine-color-default-hover)'
      }
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <UnstyledButton onClick={() => setOpen((o) => !o)} style={{ flex: 1 }}>
          <Group gap={6} wrap="nowrap" align="flex-start">
            <Box pt={3}>
              <Icon size={14} color="var(--mantine-color-dimmed)" />
            </Box>
            <Stack gap={2}>
              <Text fz="sm" fw={500}>
                {s.title}
              </Text>
              <Group gap={6}>
                <DeliveryBadge service={s} />
                <Badge size="xs" variant="outline" color="gray">
                  {recurring
                    ? (PERIOD_LABELS[s.renewalPeriod ?? ''] ?? 'Recurring')
                    : 'One-off'}
                </Badge>
                <Badge size="xs" variant="dot" color="gray">
                  {ORIGIN_LABELS[s.origin]}
                  {s.offerId ? ` #${s.offerId}` : ''}
                </Badge>
                {s.mappingStatus === 'unmapped' && (
                  <Tooltip
                    multiline
                    w={280}
                    label={
                      fullAccess
                        ? 'Not in the Product List: it can be sold again only after Business Development adds it. Review before renewals start.'
                        : 'Not in the Product List: it can be sold again only after Business Development adds it'
                    }
                  >
                    <Badge size="xs" color="yellow" variant="filled">
                      Unmapped
                    </Badge>
                  </Tooltip>
                )}
                {s.origin === 'offer' &&
                  s.deliveryState === 'pending' &&
                  s.paid === false && (
                    <Tooltip
                      label={
                        NOT_PAID_REASONS[s.paidState ?? ''] ?? 'Not paid yet'
                      }
                    >
                      <Badge size="xs" color="red" variant="light">
                        {s.paidState === 'not_assigned'
                          ? 'Not in an installment'
                          : 'Not paid'}
                      </Badge>
                    </Tooltip>
                  )}
                {recurring && (
                  <Text fz="xs" c="dimmed">
                    Next renewal: {date(s.nextRenewalDate)}
                  </Text>
                )}
                {!recurring && s.price != null && (
                  <Text fz="xs" c="dimmed">
                    {money(s.price, s.currency, s.units)}
                  </Text>
                )}
              </Group>
            </Stack>
          </Group>
        </UnstyledButton>
        {actions?.(s)}
      </Group>
      <Collapse expanded={open}>
        <Stack gap="sm" pt="sm" pl="lg">
          <Details service={s} canOpenOffers={canOpenOffers} />
          {recurring && <Cycles service={s} canWrite={canWrite} />}
          {open && !readOnly && (
            <ServiceActivity serviceId={Number(s.id)} canWrite={canWrite} />
          )}
          {open && !readOnly && <ServiceHistory serviceId={Number(s.id)} />}
        </Stack>
      </Collapse>
    </Paper>
  );
}

function Details({
  service: s,
  canOpenOffers,
}: {
  service: PortfolioService;
  canOpenOffers: boolean;
}) {
  const rows: [string, ReactNode][] = [];
  if (s.offerId)
    rows.push([
      'Offer',
      canOpenOffers ? (
        <Link to={`~/offers/${s.offerId}`}>
          {s.offerTitle ?? `#${s.offerId}`}
        </Link>
      ) : (
        (s.offerTitle ?? `#${s.offerId}`)
      ),
    ]);
  if (s.catalogName && s.catalogName !== s.title)
    rows.push(['Product List', s.catalogName]);
  if (s.mappingStatus === 'unmapped') rows.push(['Product List', 'Not mapped']);
  if (s.renewalPrice != null)
    rows.push(['Renewal price', money(s.renewalPrice, s.renewalCurrency)]);
  if (s.origin === 'monday_deal') {
    rows.push(['Price', money(s.price, s.currency)]);
    rows.push(['Units', s.units != null ? String(Number(s.units)) : '-']);
    rows.push(['Currency', s.currency ?? '-']);
  }
  if (s.deliveryState === 'delivered')
    rows.push([
      'Delivered',
      s.deliveredSource === 'monday'
        ? 'In Monday'
        : `${date(s.deliveredAt)} by ${s.deliveredByName ?? 'unknown user'}`,
    ]);
  if (s.mondayStatus && Object.keys(s.mondayStatus).length > 0)
    rows.push([
      'Monday status',
      Object.values(s.mondayStatus).filter(Boolean).join(', '),
    ]);
  if (s.notes) rows.push(['Notes', s.notes]);
  if (s.mondayItemId) rows.push(['Monday item', String(s.mondayItemId)]);

  return (
    <Stack gap={4}>
      {s.description && (
        <Text fz="xs" style={{ whiteSpace: 'pre-wrap' }}>
          {s.description}
        </Text>
      )}
      <Table fz="xs" withRowBorders={false} verticalSpacing={2}>
        <Table.Tbody>
          {rows.map(([k, v], i) => (
            <Table.Tr key={`${k}-${i}`}>
              <Table.Td c="dimmed" w={130}>
                {k}
              </Table.Td>
              <Table.Td>{v}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

const SOURCE_LABELS: Record<ServiceCycle['source'], string> = {
  monday: 'Monday',
  delivery: 'Delivery',
  manual: 'Manual',
};

function Cycles({
  service,
  canWrite,
}: {
  service: PortfolioService;
  canWrite: boolean;
}) {
  const cycles = service.cycles;
  if (cycles.length === 0)
    return (
      <Text fz="xs" c="dimmed">
        No renewal cycle yet - it starts when the service is marked delivered.
      </Text>
    );
  return (
    <Box>
      <Group justify="space-between" mb={4}>
        <Text fz="xs" fw={600} c="dimmed" tt="uppercase">
          Renewal cycles
        </Text>
        {canWrite && <CycleEdit service={service} />}
      </Group>
      <Table fz="xs" striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Cycle start</Table.Th>
            <Table.Th>Next renewal</Table.Th>
            <Table.Th>Period end</Table.Th>
            <Table.Th>Price</Table.Th>
            <Table.Th>Monday status</Table.Th>
            <Table.Th>Source</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {cycles.map((c, i) => (
            <Table.Tr key={c.id} fw={i === 0 ? 600 : undefined}>
              <Table.Td>
                {date(c.periodStart)}
                {c.startSetByName && (
                  <Text fz={10} c="dimmed" fw={400}>
                    set by {c.startSetByName}
                    {c.startSetAt ? `, ${date(c.startSetAt)}` : ''}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>{date(c.renewalDate)}</Table.Td>
              <Table.Td>{date(c.periodEnd)}</Table.Td>
              <Table.Td>{money(c.price, c.currency, c.units)}</Table.Td>
              <Table.Td>{c.mondayStatus ?? '-'}</Table.Td>
              <Table.Td>{SOURCE_LABELS[c.source] ?? c.source}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}
